// The names pages go by in the crumbs, for routes whose name loads (ADR 0030). Each reads the
// same query its page reads, so a page and its crumb never fetch twice.

import { useQuery } from "@tanstack/react-query";
import { CAMPAIGN_WORDS, callLevelLabel } from "@vd/ui";
import { bugReportQuery, callQuery } from "../api/admin";
import { campaignQuery } from "../api/campaigns";
import { useDashboardList } from "../api/dashboards";
import { conversationQuery } from "../api/messaging";
import { level, type TrailLevel } from "./crumbs";
import { fmtPhone } from "./format";

/** A call's level: "Call, Oct 23 3:15 PM" (never its contact's name). */
export function useCallName(callId: string): string | null {
  const c = useQuery(callQuery(callId)).data;
  if (!c) return null;
  return callLevelLabel(c.started_at);
}

/** A problem report's code ("R-12"). */
export function useReportName(reportId: string): string | null {
  const r = useQuery(bugReportQuery(reportId)).data;
  if (!r) return null;
  return r.number ? `R-${r.number}` : "Report";
}

/** A campaign's name. */
export function useCampaignName(id: string): string | null {
  return useQuery(campaignQuery(id)).data?.campaign.name ?? null;
}

/** Campaigns, then the campaigns above this one. */
export function useCampaignParents(id: string): TrailLevel[] {
  const d = useQuery(campaignQuery(id)).data;
  const above = (d?.path ?? []).slice(0, -1);
  return [
    level(CAMPAIGN_WORDS.screen, "/admin/campaigns"),
    ...above.map((p) => level(p.name, `/admin/campaigns/${encodeURIComponent(p.id)}`)),
  ];
}

/** A conversation's contact, or their number. */
export function useConversationName(id: string): string | null {
  const c = useQuery(conversationQuery(id)).data;
  if (!c) return null;
  return c.contact?.name ?? fmtPhone(c.contact_number);
}

/** The open dashboard's name ("Samples"); "Overview" for the built-in default. */
export function useDashboardName(dashboard: unknown): string | null {
  const list = useDashboardList();
  if (!list.data) return list.isError ? "Rep analytics" : null;
  const id = typeof dashboard === "string" ? dashboard : list.data.default_id;
  const all = [...list.data.builtins, ...list.data.mine, ...list.data.shared];
  return all.find((d) => d.id === id)?.name ?? (id === "builtin:overview" ? "Overview" : null);
}

/**
 * Rep analytics with a rep open on a dashboard ("Samples / Jared Bailey"): the dashboard is the
 * level above, by its name. Without a rep, the page is the tab's own and has no parents.
 */
export function useRepAnalyticsParents(search: Record<string, unknown>): TrailLevel[] {
  const name = useDashboardName(search.dashboard);
  if (!search.rep) return [];
  const q = typeof search.dashboard === "string" ? `?dashboard=${encodeURIComponent(search.dashboard)}` : "";
  return [level(name ?? "Rep analytics", `/admin/rep-analytics${q}`)];
}
